import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { streamAI } from "../../lib/ai";

let sid = null;
const getSessionId = () => {
  if (!sid) sid = sessionStorage.getItem("ypp_chat_sid") || `s${Date.now()}${Math.floor(Math.random() * 9999)}`;
  sessionStorage.setItem("ypp_chat_sid", sid);
  return sid;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hi! I'm Party Bot. Ask me about prices, setup days or areas." }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setBusy(true);
    try {
      await streamAI("/ai/chat", { message: text, session_id: getSessionId() }, (delta) => {
        setMsgs((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + delta };
          return copy;
        });
      });
    } catch (e) {
      setMsgs((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "Oops, I glitched. Try again or email LPontzious@msn.com!" };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            data-testid="chat-panel"
            className="sticker w-[calc(100vw-40px)] sm:w-96 rounded-[24px] bg-white overflow-hidden flex flex-col"
          >
            <div className="bg-[var(--coral)] text-white px-5 py-3 flex items-center justify-between">
              <p className="font-display font-semibold">Party Bot</p>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} aria-label="Close chat">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <p
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--sun)]" : "bg-[var(--cream)] border-2 border-[var(--ink)]"}`}
                  >
                    {m.text || "..."}
                  </p>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="border-t-2 border-[var(--ink)] p-3 flex gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about prices, setup, areas..."
                className="field !py-2.5"
              />
              <button
                data-testid="chat-send-btn"
                onClick={send}
                disabled={busy}
                aria-label="Send"
                className="shrink-0 w-11 h-11 rounded-full bg-[var(--coral)] text-white grid place-items-center sticker-sm"
              >
                <Send size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        whileHover={{ y: -3 }}
        whileTap={{ scale: 0.94 }}
        data-testid="chat-open-btn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with Party Bot"
        className="sticker w-16 h-16 rounded-full bg-[var(--sky)] text-white grid place-items-center"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </motion.button>
    </div>
  );
};
